<script>
  import { createEventDispatcher, getContext } from "svelte";

  export let label = "";
  export let description = "";
  export let complete = false;
  export let current = false;
  export let disabled = false;
  export let interactive = true;
  export let completedSteps = null;
  export let totalSteps = null;
  export let completedLabel = null;
  export let collapsible = false;
  export let expanded = false;

  const dispatch = createEventDispatcher();
  const vertical = getContext("progress-vertical") ?? false;
</script>

<li
  class="step"
  class:vertical
  class:complete={interactive && complete}
  class:current={interactive && current}
  class:disabled
  class:static={!interactive}
  on:click={interactive ? undefined : null}
  on:keypress={interactive ? undefined : null}
>
  <div class="step-indicator">
    <div class="step-icon" class:has-custom-icon={$$slots.icon}>
      {#if $$slots.icon}
        <slot name="icon" />
      {:else if complete}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6.5 11.5L2.5 7.5L3.56 6.44L6.5 9.38L12.44 3.44L13.5 4.5L6.5 11.5Z"
            fill="currentColor"
          />
        </svg>
      {:else}
        <span class="step-number"></span>
      {/if}
    </div>
    {#if !vertical}
      <div class="step-line" />
    {/if}
  </div>

  <div class="step-content">
    <div class="step-header">
      <p class="step-label bold">{label}</p>
      {#if completedSteps !== null && totalSteps !== null}
        <span class="step-separator">•</span>
        {#if collapsible}
          <button
            class="step-status step-toggle"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "Show"} completed steps for ${label}`}
            on:click={() => dispatch("toggle")}
          >
            {completedLabel || "Completed"}
            <svg class:expanded width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        {:else}
        <p class="step-status">
          {#if completedLabel && totalSteps > 0 && completedSteps === totalSteps}
            {completedLabel}
          {:else}
            {completedSteps}/{totalSteps} complete
          {/if}
        </p>
        {/if}
      {/if}
    </div>
    {#if description}
      <p class="step-description">{description}</p>
    {/if}
    {#if $$slots.default && (!collapsible || expanded)}
      <div class="step-body">
        <slot />
      </div>
    {/if}
  </div>
</li>

<style>
  .step {
    display: flex;
    align-items: flex-start;
    position: relative;
    cursor: pointer;
    min-width: 7rem;
  }

  .step.static {
    cursor: default;
  }

  .step.static .step-icon {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .step.static .step-icon.has-custom-icon {
    border: none;
    background: transparent;
    color: var(--pax-black);
  }

  .step.static .step-label {
    color: var(--pax-black);
  }

  .step.static::after {
    background: var(--pax-black) !important;
  }

  .step.vertical {
    flex-direction: row;
    padding-bottom: 1.5rem;
  }

  .step:not(.vertical) {
    flex-direction: column;
    flex: 1;
  }

  .step.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Indicator area */
  .step-indicator {
    display: flex;
    align-items: center;
    position: relative;
  }

  .step.vertical .step-indicator {
    flex-direction: column;
    align-items: center;
    margin-right: 0.75rem;
  }

  /* Icon circle */
  .step-icon {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 2px solid var(--pax-grey);
    background: var(--pax-white);
    color: var(--pax-grey);
    transition: all 0.15s ease;
    font-size: 0.75rem;
    z-index: 1;
  }

  .step.current .step-icon {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .step.complete .step-icon {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .step-icon.has-custom-icon {
    border: none;
    background: transparent;
    color: var(--pax-grey);
    width: 1.5rem;
    height: 1.5rem;
  }

  .step.current .step-icon.has-custom-icon,
  .step.complete .step-icon.has-custom-icon {
    background: transparent;
    color: var(--pax-black);
  }

  .step-number {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: currentColor;
  }

  /* Horizontal connecting line */
  .step-line {
    flex: 1;
    height: 2px;
    background: var(--pax-grey);
    margin: 0 0.25rem;
    align-self: center;
  }

  .step.complete .step-line {
    background: var(--pax-black);
  }

  /* Vertical connecting line (via parent pseudo-element) */
  .step.vertical::after {
    content: "";
    position: absolute;
    left: calc(0.75rem - 1px);
    top: 1.5rem;
    bottom: 0;
    width: 2px;
    background: var(--pax-grey);
  }

  .step.vertical.complete::after {
    background: var(--pax-black);
  }

  .step.vertical:last-child::after {
    display: none;
  }

  /* Content */
  .step-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-top: 0;
    flex: 1;
    min-width: 0;
  }

  .step:not(.vertical) .step-content {
    margin-top: 0.5rem;
  }

  .step-label {
    font-size: 0.875rem;
    color: var(--pax-grey);
    margin: 0;
    font-weight: 700;
    line-height: 100%;
  }

  .step.current .step-label,
  .step.complete .step-label {
    color: var(--pax-black);
  }

  .step-description {
    font-size: 0.75rem;
    color: var(--pax-grey);
    margin: 0;
    line-height: 1.3;
  }

  .step-body {
    margin-top: 0.5rem;
  }

  .step-header {
    display: flex;
    gap: 0.2rem;
    align-items: center;
    min-height: 1.5rem;
  }

  .step-status {
    margin: 0;
    line-height: 100%;
  }

  .step-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .step-toggle:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 2px;
  }

  .step-toggle svg.expanded {
    transform: rotate(180deg);
  }
</style>
